// Sign-in source: /pam/applications/signInSource — which directory users sign
// in with before AuthNull's second factor. POST /api/v1/idp/loginSources/*
// (internal/idp/sources.go): an Entra directory of the tenant from
// POST /ad/GetAllDomains (identitySource EntraID). AD is not available yet.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal, Select, Spin, Switch, message } from "antd";
import { useNavigate } from "react-router-dom";
import { Info, Pencil, Plus, Trash2, XCircle } from "lucide-react";

import { logOutUser } from "../../common";
import { DOMAIN_ID, ORG_ID } from "../../constants";
import { PageHeader, PageShell, StatusPill, iconButtonStyle } from "../../design-system/tableUi";
import { AN_COLORS, AN_MONO } from "../../design-system/tokens";
import { deleteLoginSource, getIdpSettings, listLoginSources, saveLoginSource } from "../../service/idpService";
import { dispatchGetAllDoamins } from "../../utils/apiservice";
import { CopyButton, FieldLabel, InlineValue, Note, PanelHead, card } from "../enduser/ssh-proxy/sshProxyUi";
import { ErrorText, Forbidden, HelpText, normaliseDomain, openIdentitySources, reportIdpError, validDomain } from "./appsUi";

const MAX_SUFFIXES = 50;

function SourceForm({ source, dirs, usedDirIds, onSaved, onCancel }) {
  const [msg, msgCtx] = message.useMessage();
  const [dirId, setDirId] = useState(source?.identitySourceId || undefined);
  const [domains, setDomains] = useState(source?.domainSuffixes || []);
  const [enabled, setEnabled] = useState(source ? !!source.enabled : true);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [saving, setSaving] = useState(false);

  const save = () => {
    const e = {};
    if (!dirId) e.dir = "Choose a directory.";
    const list = [...new Set(domains.map(normaliseDomain).filter(Boolean))];
    const bad = list.find((d) => !validDomain(d));
    if (bad) e.domains = `${bad} is not a domain name: use plain names like example.com.`;
    else if (list.length > MAX_SUFFIXES) e.domains = `At most ${MAX_SUFFIXES} domains.`;
    setErrors(e);
    setServerError("");
    if (Object.keys(e).length) return;
    setSaving(true);
    saveLoginSource({
      ...(source?.id ? { id: source.id } : {}),
      kind: "entra",
      identitySourceId: Number(dirId),
      domainSuffixes: list,
      disabled: !enabled,
    })
      .then((res) => {
        msg.success("Sign-in source saved.");
        onSaved(res?.loginSource);
      })
      .catch((err) =>
        reportIdpError(err, { msg, fallback: "Could not save the sign-in source.", setInline: setServerError, onNotFound: () => onSaved(null) }),
      )
      .finally(() => setSaving(false));
  };

  return (
    <div style={{ ...card, padding: 18 }}>
      {msgCtx}
      <PanelHead title={source?.id ? "Edit sign-in source" : "Add a sign-in source"} />
      <div style={{ display: "grid", gap: 16 }}>
        <div>
          <FieldLabel required>Directory</FieldLabel>
          <Select
            value={dirId}
            onChange={(v) => {
              setDirId(v);
              setServerError("");
            }}
            placeholder={dirs.length ? "Choose an Entra directory" : "No Entra directory yet"}
            disabled={!dirs.length}
            status={errors.dir ? "error" : undefined}
            options={dirs.map((d) => ({
              value: d.id,
              label: `${d.directoryName || `Directory ${d.id}`}${d.domainName ? ` · ${d.domainName}` : ""}`,
              disabled: usedDirIds.has(d.id) && d.id !== source?.identitySourceId,
            }))}
            style={{ width: "100%", maxWidth: "28rem" }}
          />
          <HelpText>Entra ID directories only: signing in with Active Directory is not available yet.</HelpText>
          <ErrorText>{errors.dir}</ErrorText>
        </div>

        <div>
          <FieldLabel hint="optional">Email domains</FieldLabel>
          <Select
            mode="tags"
            value={domains}
            onChange={(v) => {
              setDomains(v);
              setServerError("");
            }}
            tokenSeparators={[",", " "]}
            open={false}
            placeholder="example.com"
            status={errors.domains ? "error" : undefined}
            style={{ width: "100%", maxWidth: "28rem" }}
          />
          <HelpText>
            The email domains this directory signs in. Needed only when a tenant has more than one source.
          </HelpText>
          <ErrorText>{errors.domains}</ErrorText>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Switch checked={enabled} onChange={setEnabled} />
          <span style={{ fontSize: 13, color: AN_COLORS.ink2 }}>{enabled ? "Enabled" : "Disabled: nobody signs in through it"}</span>
        </div>

        {serverError && (
          <Note tone="danger" icon={<XCircle size={15} />}>
            {serverError}
          </Note>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <Button type="primary" loading={saving} disabled={!dirs.length} onClick={save}>
            {source?.id ? "Save changes" : "Save sign-in source"}
          </Button>
          {onCancel && (
            <Button onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SignInSource() {
  const navigate = useNavigate();
  const [msg, msgCtx] = message.useMessage();
  const [modal, modalCtx] = Modal.useModal();

  const [sources, setSources] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [dirs, setDirs] = useState(null);
  const [endpoints, setEndpoints] = useState(null);
  // null: none open; {}: adding; a source: editing it.
  const [editing, setEditing] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let alive = true;
    listLoginSources()
      .then((res) => {
        if (!alive) return;
        const list = res?.loginSources || [];
        setSources(list);
        // With none yet, the form is open.
        setEditing((cur) => (list.length === 0 ? cur || {} : null));
      })
      .catch((err) => {
        if (!alive) return;
        reportIdpError(err, { msg, fallback: "Could not read the sign-in sources.", setForbidden });
        setSources([]);
      });
    return () => {
      alive = false;
    };
  }, [reloadKey, msg]);

  useEffect(() => {
    let alive = true;
    getIdpSettings()
      .then((res) => alive && setEndpoints(res?.endpoints || {}))
      .catch(() => alive && setEndpoints({}));
    dispatchGetAllDoamins({
      payload: {
        tenantId: DOMAIN_ID(),
        orgId: ORG_ID(),
        pageNo: 1,
        pageSize: 100,
        filters: { identitySource: "EntraID", status: "Active" },
      },
      onSuccess: (res) => alive && setDirs((res?.domains || []).filter((d) => d?.status !== "Deleted")),
      onFailure: ({ message: m, isLogout } = {}) => {
        if (isLogout) return logOutUser();
        if (alive) setDirs([]);
        return msg.error(m || "Could not read the directories.");
      },
      onError: ({ message: m } = {}) => {
        if (alive) setDirs([]);
        msg.error(m || "Could not read the directories.");
      },
    });
    return () => {
      alive = false;
    };
  }, [msg]);

  const usedDirIds = useMemo(() => new Set((sources || []).map((s) => s.identitySourceId)), [sources]);
  const dirName = (s) => s.identitySourceName || (dirs || []).find((d) => d.id === s.identitySourceId)?.directoryName || `Directory ${s.identitySourceId}`;

  const confirmDelete = (s) =>
    modal.confirm({
      title: `Remove the sign-in source ${dirName(s)}?`,
      content: "Users of this directory will no longer be able to sign in to applications through AuthNull.",
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: () =>
        deleteLoginSource(s.id)
          .then(() => {
            msg.success("Sign-in source removed.");
            reload();
          })
          .catch((err) => reportIdpError(err, { msg, fallback: "Could not remove the sign-in source.", onNotFound: reload })),
    });

  const loading = sources === null || dirs === null;
  const redirectUri = endpoints?.upstreamRedirectUri;

  let body;
  if (forbidden) body = <Forbidden />;
  else if (loading)
    body = (
      <div style={{ padding: 60, textAlign: "center" }}>
        <Spin />
      </div>
    );
  else
    body = (
      <div style={{ display: "grid", gap: 16, maxWidth: "56rem" }}>
        {dirs.length === 0 && (
          <Note tone="warn" icon={<Info size={15} />}>
            No Entra directory is connected to this tenant yet.{" "}
            <Button type="link" size="small" style={{ padding: 0, height: "auto" }} onClick={() => openIdentitySources(navigate)}>
              Connect one in Identity → Identity Sources
            </Button>
          </Note>
        )}

        {sources.length > 0 && (
          <div style={{ ...card, padding: 18 }}>
            <PanelHead
              title="Sign-in sources"
              right={
                !editing && dirs.length > usedDirIds.size ? (
                  <Button icon={<Plus size={13} style={{ verticalAlign: -2 }} />} onClick={() => setEditing({})}>
                    Add source
                  </Button>
                ) : null
              }
            />
            {sources.map((s, i) => (
              <div
                key={s.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 0",
                  borderTop: i ? `1px solid ${AN_COLORS.line2}` : "none",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ flex: 1, minWidth: "12rem" }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: AN_COLORS.ink }}>
                    {dirName(s)} <span style={{ fontWeight: 400, color: AN_COLORS.muted, fontSize: 12 }}>· Entra ID</span>
                  </div>
                  <div style={{ fontSize: 12, color: AN_COLORS.muted, marginTop: 3 }}>
                    {s.domainSuffixes?.length ? (
                      <>
                        Email domains:{" "}
                        <span style={{ fontFamily: AN_MONO, color: AN_COLORS.ink3 }}>{s.domainSuffixes.join(", ")}</span>
                      </>
                    ) : (
                      "Any email domain"
                    )}
                  </div>
                </div>
                <StatusPill tone={s.enabled ? "success" : "neutral"} label={s.enabled ? "Enabled" : "Disabled"} />
                <button type="button" aria-label={`Edit ${dirName(s)}`} style={iconButtonStyle()} onClick={() => setEditing(s)}>
                  <Pencil size={14} />
                </button>
                <button
                  type="button"
                  aria-label={`Remove ${dirName(s)}`}
                  style={{ ...iconButtonStyle(), color: AN_COLORS.danger }}
                  onClick={() => confirmDelete(s)}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        {editing && (
          <SourceForm
            key={editing.id || "new"}
            source={editing.id ? editing : null}
            dirs={dirs}
            usedDirIds={usedDirIds}
            onSaved={() => {
              setEditing(null);
              reload();
            }}
            onCancel={sources.length ? () => setEditing(null) : undefined}
          />
        )}

        <div style={{ ...card, padding: 18 }}>
          <PanelHead
            title="Register the redirect URI in Azure"
            desc="Entra sends users back to AuthNull here after they sign in. Without it, Entra refuses the sign-in."
          />
          <div style={{ fontSize: 13, color: AN_COLORS.ink2, lineHeight: 1.6, marginBottom: 10 }}>
            In Azure, open the Entra app registration used for this directory → <b>Authentication</b> → <b>Add a platform</b> →{" "}
            <b>Web</b>, and add this redirect URI:
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 12px",
              border: `1px solid ${AN_COLORS.line}`,
              borderRadius: 8,
              background: AN_COLORS.tint,
            }}
          >
            <code style={{ flex: 1, minWidth: 0, fontFamily: AN_MONO, fontSize: 12.5, color: redirectUri ? AN_COLORS.ink : AN_COLORS.muted, wordBreak: "break-all" }}>
              {redirectUri || (endpoints ? "Unavailable" : "Loading…")}
            </code>
            {redirectUri && <CopyButton text={redirectUri} />}
          </div>
          <HelpText>
            The app registration is the one whose client ID and secret are set on the directory in Identity Sources. Use the
            Web platform, not <InlineValue>Single-page application</InlineValue>.
          </HelpText>
        </div>
      </div>
    );

  return (
    <PageShell>
      {msgCtx}
      {modalCtx}
      <PageHeader
        eyebrow="Applications"
        title="Sign-in source"
        subtitle="The directory users sign in with before AuthNull asks for their second factor."
      />
      {body}
    </PageShell>
  );
}
